import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, LogOut, Trash2, Phone, Mail } from "lucide-react";
import { Logo } from "@/components/Logo";
import { usePageMeta } from "@/components/Common";
import { API } from "@/data/site";

const http = axios.create({ baseURL: API, withCredentials: true });
const STATUSES = ["new", "contacted", "closed"];

const Login = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      onDone(data);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Login failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="admin-login-form" className="mx-auto mt-10 max-w-md rounded-[4px] bg-white p-8 shadow-xl">
      <h1 className="font-display text-3xl font-semibold text-[#14263D]">Owner sign in</h1>
      <label className="mt-6 block text-sm text-[#14263D]">Email<input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 w-full rounded-[3px] border border-[#14263D]/25 px-4 py-3" /></label>
      <label className="mt-4 block text-sm text-[#14263D]">Password<input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 w-full rounded-[3px] border border-[#14263D]/25 px-4 py-3" /></label>
      {err && <p data-testid="admin-login-error" role="alert" className="mt-4 text-sm text-[#A52A32]">{err}</p>}
      <button data-testid="admin-login-submit" disabled={busy} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#A52A32] py-3 font-semibold text-white">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}Sign in
      </button>
    </form>
  );
};

const Row = ({ r, onStatus, onDelete }) => (
  <article data-testid={`submission-${r.id}`} className="rounded-[4px] border border-[#14263D]/10 bg-white p-6">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <p className="font-display text-2xl font-semibold text-[#14263D]">{r.full_name}</p>
        <p className="mt-1 font-mono text-xs text-[#5F6B76]">{new Date(r.created_at).toLocaleString()}</p>
      </div>
      <div className="flex items-center gap-2">
        <select data-testid={`submission-status-${r.id}`} value={r.status} onChange={(e) => onStatus(r.id, e.target.value)} className="rounded-full border border-[#14263D]/25 px-3 py-1.5 text-sm capitalize">
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button data-testid={`submission-delete-${r.id}`} onClick={() => onDelete(r.id)} aria-label="Delete submission" className="rounded-full p-2 text-[#A52A32] hover:bg-[#A52A32]/10"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
    <div className="mt-4 flex flex-wrap gap-4 text-sm">
      <a href={`tel:${r.phone}`} className="flex items-center gap-2 text-[#14263D] underline-offset-4 hover:underline"><Phone className="h-4 w-4 text-[#A52A32]" />{r.phone}</a>
      <a href={`mailto:${r.email}`} className="flex items-center gap-2 text-[#14263D] underline-offset-4 hover:underline"><Mail className="h-4 w-4 text-[#A52A32]" />{r.email}</a>
    </div>
    <dl className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
      {[["City", r.city], ["Address", r.property_address], ["Sq ft", r.square_footage], ["Property", r.property_type], ["Dates", r.absence_dates], ["Frequency", r.visits_per_month], ["Service type", r.service_type], ["Concierge", (r.concierge_services ?? []).join(", ")], ...(r.package ? [["Package", r.package]] : []), ["Contact via", r.contact_method]].map(([k, v]) => (
        <div key={k}><dt className="inline font-mono text-xs uppercase text-[#5F6B76]">{k}: </dt><dd className="inline text-[#14263D]">{v || "—"}</dd></div>
      ))}
    </dl>
    {r.comments && <p className="mt-4 rounded-[3px] bg-[#F8F7F3] p-4 text-sm text-[#14263D]">{r.comments}</p>}
  </article>
);

export default function Admin() {
  usePageMeta("Admin | First Due", "");
  const [user, setUser] = useState(null);
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/consultations");
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    }
  }, []);

  useEffect(() => {
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const onStatus = async (id, status) => {
    await http.patch(`/consultations/${id}`, { status });
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    await http.delete(`/consultations/${id}`);
    setRows((rs) => rs.filter((r) => r.id !== id));
  };
  const logout = async () => {
    await http.post("/auth/logout");
    setUser(false);
  };

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#F8F7F3] pb-20">
      <div className="bg-[#14263D] px-5 py-5 md:px-10">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Logo light />
          {user && <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 text-sm text-white"><LogOut className="h-4 w-4" />Sign out</button>}
        </div>
      </div>
      <div className="mx-auto max-w-6xl px-5 md:px-10">
        {user === null && <Loader2 className="mx-auto mt-20 h-6 w-6 animate-spin text-[#14263D]" />}
        {user === false && <Login onDone={setUser} />}
        {user && (
          <>
            <div className="mt-10 flex items-end justify-between">
              <h1 className="font-display text-4xl font-semibold text-[#14263D]">Consultation requests</h1>
              <p data-testid="admin-count" className="font-mono text-sm text-[#5F6B76]">{rows.length} total</p>
            </div>
            <div className="mt-8 space-y-4">
              {rows.length === 0 && <p data-testid="admin-empty" className="text-[#5F6B76]">No requests yet.</p>}
              {rows.map((r) => <Row key={r.id} r={r} onStatus={onStatus} onDelete={onDelete} />)}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
